import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { useState } from "react"
import { z } from "zod"
import { gql } from "~/__generated__"
import { VisibilityContactIndex, VisibilityWalletIndex } from "~/__generated__/graphql"
import { handlePath } from "~/common/paths"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useValidationErrors } from "~/common/use-validation-errors"
import { directImageUpload } from "~/common/direct-image-upload"
import { toast } from "~/ui/use-toast"

const UPDATE_PROFILE_MUTATION = gql(/* GraphQL */ `
  mutation UpdateProfile($input: UserUpdateInput!) {
    userUpdate(input: $input) {
      user {
        id
        firstName
        lastName
        fullName
        handle
        bio
        avatarUrl
        avatarThumbUrl
        visibilityWalletIndex
        visibilityContactIndex
      }
    }
  }
`)

const editProfileSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  handle: z.string().min(1, "Handle is required").min(3, "Handle must be at least 3 characters"),
  bio: z.string(),
  visibilityWalletIndex: z.nativeEnum(VisibilityWalletIndex),
  visibilityContactIndex: z.nativeEnum(VisibilityContactIndex),
})

type AvatarChange = { type: "upload"; signedId: string } | { type: "remove" } | null

export const useProfileEditor = (user: any, onSuccess?: () => void) => {
  const navigate = useNavigate()
  const [avatarChange, setAvatarChange] = useState<AvatarChange | null>(null)
  const [previewAvatarUrl, setPreviewAvatarUrl] = useState<string | null>(null)
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)

  const [updateProfile, updateResult] = useSafeMutation(UPDATE_PROFILE_MUTATION, {
    onCompleted: (data) => {
      onSuccess?.()
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated.",
      })

      // If handle was changed, navigate to the new profile URL
      const newHandle = data.userUpdate.user.handle
      if (newHandle && newHandle !== user?.handle) {
        navigate(handlePath({ handle: newHandle }))
      }
    },
  })

  const form = useForm<z.infer<typeof editProfileSchema>>({
    resolver: zodResolver(editProfileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      handle: "",
      bio: "",
      visibilityWalletIndex: VisibilityWalletIndex.Private,
      visibilityContactIndex: VisibilityContactIndex.Private,
    },
  })

  useValidationErrors(form.setError, updateResult)

  const initializeForm = (userData: any) => {
    form.reset({
      firstName: userData.firstName ?? "",
      lastName: userData.lastName ?? "",
      handle: userData.handle ?? "",
      bio: userData.bio ?? "",
      visibilityWalletIndex: userData.visibilityWalletIndex ?? VisibilityWalletIndex.Private,
      visibilityContactIndex: userData.visibilityContactIndex ?? VisibilityContactIndex.Private,
    })
    setAvatarChange(null)
    setPreviewAvatarUrl(null)
  }

  const onSubmit = async (values: z.infer<typeof editProfileSchema>) => {
    if (!user) return

    try {
      const input: any = {
        id: user.id,
        userInput: {
          firstName: values.firstName,
          lastName: values.lastName,
          handle: values.handle,
          bio: values.bio,
          visibilityWalletIndex: values.visibilityWalletIndex,
          visibilityContactIndex: values.visibilityContactIndex,
        },
      }

      // Apply avatar changes if any
      if (avatarChange?.type === "upload") {
        input.avatarSignedId = avatarChange.signedId
      } else if (avatarChange?.type === "remove") {
        input.removeAvatar = true
      }

      await updateProfile({
        variables: { input },
      })

      // Reset avatar state after successful save
      setAvatarChange(null)
      setPreviewAvatarUrl(null)
    } catch (error) {
      console.error("Failed to update profile:", error)
    }
  }

  const handleAvatarUpload = async (file: File) => {
    setIsUploadingAvatar(true)

    try {
      const { signedId } = await directImageUpload(file)

      // Create preview URL for immediate feedback
      const previewUrl = URL.createObjectURL(file)
      setPreviewAvatarUrl(previewUrl)

      // Store the signed ID to be applied on save
      setAvatarChange({ type: "upload", signedId })
    } catch (error) {
      console.error("Avatar upload error:", error)
      toast({
        title: "Upload failed",
        description: "There was an error uploading your avatar. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleRemoveAvatar = () => {
    setAvatarChange({ type: "remove" })
    setPreviewAvatarUrl(null)
  }

  const resetChanges = () => {
    setAvatarChange(null)
    setPreviewAvatarUrl(null)
  }

  return {
    form,
    onSubmit,
    updateResult,
    avatarChange,
    previewAvatarUrl,
    isUploadingAvatar,
    handleAvatarUpload,
    handleRemoveAvatar,
    initializeForm,
    resetChanges,
  }
}
